// editNarrative.js (frontend)
//
// The server classifies every changed element from its hashes and hands
// back a verb (utils/editNarrative.js on the backend). That gets you as
// far as "moved or rotated" and no further, because `placementHash`
// quantizes the world AABB and the basis into ONE value - a move and a
// rotation are the same event as far as it is concerned.
//
// The drawer has something better: both instance MATRICES, the actual
// world transforms out of each version's tile sidecars. Those decompose,
// so here the sentence can be exact - "moved 4 ft 3 in", "rotated 90°
// about the vertical axis", or both - and it degrades back to the
// server's wording when the old geometry is not available.
//
// Units are FEET. Nothing in the manifest declares that, but the
// exporter's own fragment keys are written in feet ("l96_67ft_h20ft")
// and the scene bounds of a known sample building come out at building
// scale in feet. The unit is printed next to every number so a wrong
// assumption would be visible rather than silent.
import { Matrix4, Vector3, Quaternion } from "three";

// Below these, a difference is export noise rather than an edit.
const MOVE_EPS_FT = 0.01;      // ~1/8 inch
const ROT_EPS_DEG = 0.5;

export const VERB_LABEL = {
  replaced: "replaced",
  recut: "recut",
  reshaped: "reshaped",
  relocated: "moved or rotated",
  retagged: "data changed",
  added: "added",
  deleted: "deleted",
  moved: "moved",
  rotated: "rotated",
  movedAndRotated: "moved and rotated",
};

// The diff's colours already say added/removed/modified; these tint the
// verb chip so a list scans by what happened, not just by bucket.
export const VERB_COLOR = {
  replaced: "#8E44AD",
  recut: "#B7791F",
  reshaped: "#E0A43A",
  relocated: "#5B8DEF",
  moved: "#5B8DEF",
  rotated: "#2C82C9",
  movedAndRotated: "#2C82C9",
  retagged: "#7B8794",
  added: "#4FC92A",
  deleted: "#FB8080",
};

export function formatFeet(ft) {
  const v = Math.abs(Number(ft) || 0);
  const feet = Math.floor(v);
  const inches = (v - feet) * 12;
  if (feet >= 1) {
    const inRounded = Math.round(inches);
    // 11.6" rounds to 12" — carry it rather than print "4 ft 12 in".
    if (inRounded >= 12) return `${feet + 1} ft`;
    return inRounded ? `${feet} ft ${inRounded} in` : `${feet} ft`;
  }
  if (inches >= 1) return `${inches.toFixed(inches < 3 ? 1 : 0)} in`;
  return `${inches.toFixed(2)} in`;
}

function decompose(matrix16) {
  if (!Array.isArray(matrix16) || matrix16.length !== 16) return null;
  // The sidecar stores the column-vector matrix written row-major, which
  // is the order Matrix4.set takes (see instanceMaterializer.js).
  const m = new Matrix4().set(...matrix16);
  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  m.decompose(position, quaternion, scale);
  return { position, quaternion, scale };
}

/**
 * Compare two instance placements.
 * @returns {{ distanceFt, rotationDeg, axis: Vector3|null, scaled: boolean }|null}
 */
export function comparePlacement(oldMatrix, newMatrix) {
  const a = decompose(oldMatrix);
  const b = decompose(newMatrix);
  if (!a || !b) return null;

  const distanceFt = a.position.distanceTo(b.position);

  // Angle between orientations, via the shortest arc. The abs() picks the
  // shorter of q and -q, which represent the same rotation.
  const dot = Math.min(1, Math.abs(a.quaternion.dot(b.quaternion)));
  const rotationDeg = (2 * Math.acos(dot) * 180) / Math.PI;

  const delta = b.quaternion.clone().multiply(a.quaternion.clone().invert()).normalize();
  let axis = null;
  if (rotationDeg > ROT_EPS_DEG) {
    const s = Math.sqrt(Math.max(0, 1 - delta.w * delta.w));
    axis = s > 1e-6
      ? new Vector3(delta.x / s, delta.y / s, delta.z / s)
      : null;
  }

  const scaled = Math.abs(a.scale.length() - b.scale.length()) > 1e-4;
  return { distanceFt, rotationDeg, axis, scaled };
}

function axisName(axis) {
  if (!axis) return "";
  const ax = Math.abs(axis.x);
  const ay = Math.abs(axis.y);
  const az = Math.abs(axis.z);
  // Y is vertical in the exported scene (the building's short dimension
  // in every manifest's bounds), so a rotation about it is the one people
  // mean when they say "I spun it round".
  if (ay >= ax && ay >= az) return " about the vertical axis";
  if (ax >= az) return " about the X axis";
  return " about the Z axis";
}

/**
 * Turn one fragment from GET /commits/:id/edit-fragments into plain text.
 * Uses the matrices when both sides are present, and falls back to the
 * server's hash verdict when they are not.
 *
 * @returns {{ verb, headline, evidence: string[], precise: boolean }}
 */
export function describeFragment(f) {
  if (!f) return { verb: "", headline: "", evidence: [], precise: false };
  const label = [f.category, f.typeName || f.family].filter(Boolean).join(" ") || "element";
  const evidence = [];
  if (f.why) evidence.push(f.why);

  if (f.change === "added") {
    return { verb: "added", headline: `Added this ${(f.category || "element").toLowerCase()}`, evidence, precise: true };
  }
  if (f.change === "removed") {
    return { verb: "deleted", headline: `Deleted this ${(f.category || "element").toLowerCase()}`, evidence, precise: true };
  }

  const cmp = comparePlacement(f.old?.instance?.matrix, f.new?.instance?.matrix);

  // Only the pose verdict can be sharpened by the matrices. A type swap
  // or a recut is a statement about geometry, and the transforms have
  // nothing to add to it beyond whether it also moved.
  if (f.verb === "relocated") {
    if (!cmp) {
      return {
        verb: "relocated",
        headline: `Moved or rotated — ${label}`,
        evidence: [...evidence, "the earlier export's placement is gone, so the two cannot be told apart here"],
        precise: false,
      };
    }
    const moved = cmp.distanceFt > MOVE_EPS_FT;
    const turned = cmp.rotationDeg > ROT_EPS_DEG;
    if (moved && turned) {
      return {
        verb: "movedAndRotated",
        headline: `Moved ${formatFeet(cmp.distanceFt)} and rotated ${Math.round(cmp.rotationDeg)}°${axisName(cmp.axis)} — ${label}`,
        evidence: [...evidence, "same geometry on both sides, so this is a pure change of pose"],
        precise: true,
      };
    }
    if (turned) {
      return {
        verb: "rotated",
        headline: `Rotated ${Math.round(cmp.rotationDeg)}°${axisName(cmp.axis)} — ${label}`,
        evidence: [...evidence, "position identical to the fraction of an inch"],
        precise: true,
      };
    }
    if (moved) {
      return {
        verb: "moved",
        headline: `Moved ${formatFeet(cmp.distanceFt)} — ${label}`,
        evidence: [...evidence, "orientation unchanged"],
        precise: true,
      };
    }
    return {
      verb: "relocated",
      headline: `Placement recorded as changed, but the transforms match — ${label}`,
      evidence: [...evidence, "the placement hash also covers the bounding box, so a resize can read as a move"],
      precise: true,
    };
  }

  const extra = [];
  if (cmp && cmp.distanceFt > MOVE_EPS_FT) extra.push(`it also moved ${formatFeet(cmp.distanceFt)}`);
  if (cmp && cmp.rotationDeg > ROT_EPS_DEG) extra.push(`and turned ${Math.round(cmp.rotationDeg)}°`);

  const HEAD = {
    replaced: `Replaced — ${label}`,
    recut: `Recut in place — ${label}`,
    reshaped: `Reshaped — ${label}`,
    retagged: `Data changed — ${label}`,
  };
  return {
    verb: f.verb || "retagged",
    headline: HEAD[f.verb] || `Changed — ${label}`,
    evidence: [...evidence, ...extra],
    precise: !!cmp,
  };
}

/**
 * The whole push in one line, refined with whatever the matrices proved.
 * Falls back to the server's sentence when nothing could be refined.
 */
export function describePush(payload) {
  const fragments = payload?.fragments || [];
  const serverText = payload?.narrative?.text || "";
  if (!fragments.length) return { text: serverText || "Nothing changed in this push.", refined: false };

  const counts = new Map();
  let refined = false;
  for (const f of fragments) {
    const d = describeFragment(f);
    if (d.verb === "moved" || d.verb === "rotated" || d.verb === "movedAndRotated") refined = true;
    const noun = (f.category || "element").toLowerCase();
    const key = `${d.verb}|${noun}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  if (!refined) return { text: serverText, refined: false };

  // Rebuild the sentence with the sharper verbs, in the same shape the
  // server uses so the two never read as different findings.
  const ORDER = ["replaced", "recut", "reshaped", "movedAndRotated", "moved", "rotated", "relocated", "retagged", "added", "deleted"];
  const PHRASE = {
    replaced: "Replaced", recut: "Recut", reshaped: "Reshaped",
    movedAndRotated: "Moved and rotated", moved: "Moved", rotated: "Rotated",
    relocated: "Moved or rotated", retagged: "Changed data on",
    added: "Added", deleted: "Deleted",
  };
  const byVerb = new Map();
  for (const [key, n] of counts) {
    const [verb, noun] = key.split("|");
    if (!byVerb.has(verb)) byVerb.set(verb, []);
    byVerb.get(verb).push([noun, n]);
  }
  const parts = [];
  for (const verb of ORDER) {
    const cats = byVerb.get(verb);
    if (!cats) continue;
    cats.sort((a, b) => b[1] - a[1]);
    const shown = cats.slice(0, 3).map(([noun, n]) => `${n} ${n === 1 ? singular(noun) : plural(singular(noun))}`);
    const rest = cats.slice(3).reduce((s, [, n]) => s + n, 0);
    if (rest) shown.push(`${rest} other${rest === 1 ? "" : "s"}`);
    parts.push(`${PHRASE[verb]} ${shown.join(", ")}`);
  }
  return { text: `${parts.join(". ")}.`, refined: true };
}

// Category names arrive plural and title-cased from Revit.
const NOUNS = {
  "mechanical equipment": "mechanical unit",
  doors: "door", walls: "wall", windows: "window", floors: "floor",
  ceilings: "ceiling", roofs: "roof", ducts: "duct", pipes: "pipe",
  "duct fittings": "duct fitting", "pipe fittings": "pipe fitting",
  "air terminals": "air terminal", "lighting fixtures": "light fitting",
  "plumbing fixtures": "plumbing fixture", "structural framing": "beam",
  "structural columns": "column", "generic models": "generic model",
};
function singular(category) {
  const k = String(category || "").trim().toLowerCase();
  if (!k) return "element";
  if (NOUNS[k]) return NOUNS[k];
  if (/(ss|sh|ch|x)$/.test(k)) return k;
  return k.replace(/ies$/, "y").replace(/s$/, "");
}
function plural(noun) {
  if (/(s|sh|ch|x)$/.test(noun)) return `${noun}es`;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  return `${noun}s`;
}
